/**
 * StopABTestModal
 * ----------------
 * Shown when an admin stops an A/B test that's already been SAVED (i.e. a
 * real, running/drafted experiment exists server-side) from inside a bundle
 * editor. Reuses the exact same table + "set winner" flow as the main A/B
 * Test dashboard (ABTestDashboard.tsx via ABTestPanel), just scoped to a
 * single bundle via the `form_id` filter on `/ab-test/list`, with the
 * Running/Draft/Completed tabs and category filter hidden — this bundle's
 * status is already known, there's nothing to filter.
 */
import { useEffect, useState } from "react";
import CustomModal from "../../components/CustomModal/CustomModal";
import { endPoints, getData } from "../../utilities/networkCalls";
import { ABTestPanel } from "./components";
import { ABTestRow, ABTestStatus } from "./types";

interface StopABTestModalProps {
    open: boolean;
    onClose: () => void;
    formId: string;
    /** e.g. "volume" — which bundle type this bundle is, used to scope the AB test lookup. */
    bundleType: string;
    isPublished: boolean;
    /** Called with the freshly-updated bundle once a winner has been applied, so the caller can resync its editor. */
    onWinnerApplied: (bundle: any) => void;
}

const StopABTestModal = ({ open, onClose, formId, bundleType, isPublished, onWinnerApplied }: StopABTestModalProps) => {
    const status: ABTestStatus = isPublished ? "running" : "draft";
    const [rows, setRows] = useState<ABTestRow[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !formId) return;
        let cancelled = false;
        setLoading(true);
        (async () => {
            const res = await getData({
                url: endPoints.listAbTests + `?status=${status}&bundle_type=${bundleType}&form_id=${formId}`,
            });
            if (cancelled) return;
            setRows(res?.status === 200 ? res.data?.abTests || [] : []);
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [open, formId, status, bundleType]);

    return (
        <CustomModal
            title="Stop A/B Testing"
            open={open}
            size="large"
            onClose={onClose}
            secondaryActions={[{ content: "Close", onAction: onClose }]}
        >
            <ABTestPanel
                rows={rows}
                loading={loading}
                status={status}
                selectedTabIndex={0}
                onTabChange={() => {}}
                selectedBundleTypes={[bundleType]}
                onBundleTypesChange={() => {}}
                hideFilters
                isBundleFormContext
                onWinnerSet={(bundle) => {
                    onClose();
                    onWinnerApplied(bundle);
                }}
            />
        </CustomModal>
    );
};

export default StopABTestModal;
